/**
 * Microsoft Teams integration settings.
 *
 * Drives the customer-facing Teams install flow. Three pieces:
 *   1. Click "Install Canary in Teams" → admin-consent OAuth round-trip
 *      with Microsoft → tenant id auto-bound to this org.
 *   2. Download the Teams app .zip → upload in Teams Admin Center.
 *   3. Add the bot to a channel and @-mention it.
 *
 * The manual tenant-id paste is kept under an "Advanced" disclosure for
 * design partners whose admins can't click through the consent flow (e.g.
 * tenants with admin consent locked down) — the paste still works because
 * the binding endpoint never trusted the OAuth tenant id over the form
 * value to begin with.
 *
 * @module ui/components/settings/TeamsSettingsSection
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Flex,
  Heading,
  LoadingOrbit,
  Text,
  TextField,
  Warning,
  CheckCircle,
  useToast,
  TagBadge,
} from '@chatsdet/ui';
import {
  LinkSimple,
  DownloadSimple,
  ArrowSquareOut,
  ClipboardText,
  MicrosoftTeamsLogo,
  CaretRight,
  CaretDown,
} from '@phosphor-icons/react';
import { API_URL } from '../../../config.js';
import { toApiError } from '../../../lib/api-error';
import { BRAND } from '../../../brand';
import { getTeamsInstallErrorToast } from './teams-install-toast';

type TeamsConnection = {
  ok: boolean;
  tenantId: string | null;
  teamsAppId: string | null;
  isBound: boolean;
  isInCatalog: boolean;
  isFlagEnabled: boolean;
  installCount: number;
};

/** The Teams install start (Install button, and `connect=teams` from `cofactor teams install`). */
export function startTeamsInstall(): void {
  window.location.href = `${API_URL}/integrations/teams/install`;
}

export async function fetchTeamsConnection(): Promise<TeamsConnection> {
  const res = await fetch(`${API_URL}/integrations/teams/connection`, {
    credentials: 'include',
  });
  if (!res.ok) throw await toApiError(res);
  return res.json();
}

const TENANT_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function TeamsSettingsSection() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const name = BRAND.name;

  const connectionQuery = useQuery({
    queryKey: ['teams-connection'],
    queryFn: fetchTeamsConnection,
  });

  const bindMutation = useMutation({
    mutationFn: async ({ tenantId }: { tenantId: string }) => {
      const res = await fetch(`${API_URL}/integrations/teams/connection`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ tenantId }),
      });
      if (!res.ok) throw await toApiError(res);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teams-connection'] });
      toast.success('Microsoft tenant bound — install the Teams app and @-mention the bot to test.');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to bind Microsoft tenant.');
    },
  });

  const unbindMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/integrations/teams/connection`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teams-connection'] });
      toast.success('Microsoft tenant unbound.');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to unbind Microsoft tenant.');
    },
  });

  const [tenantInput, setTenantInput] = React.useState('');
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  React.useEffect(() => {
    if (connectionQuery.data?.tenantId) {
      setTenantInput(connectionQuery.data.tenantId);
    }
  }, [connectionQuery.data?.tenantId]);

  // Surface the round-trip result from the admin-consent OAuth callback.
  // The callback redirects back here with `install=success|error[&reason=…]`;
  // we toast, refresh the connection status, and scrub the query params so
  // a page reload doesn't re-fire the toast.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const result = params.get('install');
    if (!result) return;
    if (result === 'success') {
      toast.success(
        `${name} installed in your Teams tenant catalog — open in Teams to add to a channel.`
      );
      void qc.invalidateQueries({ queryKey: ['teams-connection'] });
    } else if (result === 'error') {
      const reason = params.get('reason') ?? '';
      const errorToast = getTeamsInstallErrorToast(reason);
      toast.error(errorToast.message, { reportError: errorToast.reportError });
    }
    params.delete('install');
    params.delete('reason');
    const next = params.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (next ? `?${next}` : '')
    );
  }, [qc, toast]);

  if (connectionQuery.isLoading) {
    return (
      <Box className="flex justify-center py-12">
        <LoadingOrbit />
      </Box>
    );
  }

  const data = connectionQuery.data;
  const isBound = data?.isBound ?? false;
  const isInCatalog = data?.isInCatalog ?? false;
  const teamsAppId = data?.teamsAppId ?? null;
  const installCount = data?.installCount ?? 0;
  const isFlagEnabled = data?.isFlagEnabled ?? false;
  const tenantValid = TENANT_ID_RE.test(tenantInput.trim());
  const tenantChanged = tenantInput.trim() !== (data?.tenantId ?? '');

  // Deep link to the Canary app's page in the customer tenant's Teams
  // catalog. Microsoft renders an "Add" button on that page, so this
  // collapses "go to Teams + find the app + click Add" into one click.
  const teamsDeepLink = teamsAppId
    ? `https://teams.microsoft.com/l/app/${teamsAppId}?source=settings`
    : null;

  // Four states drive the status card chrome:
  // - connected:   bound + bot has joined at least one channel
  // - in-catalog:  bound + manifest published, but no channel installs yet
  // - pending:     bound but manifest not in catalog (legacy paste-only flow)
  // - unconnected: nothing
  const state: 'connected' | 'in-catalog' | 'pending' | 'unconnected' =
    isBound && installCount > 0
      ? 'connected'
      : isBound && isInCatalog
        ? 'in-catalog'
        : isBound
          ? 'pending'
          : 'unconnected';

  return (
    <Box className="space-y-6">
      {!isFlagEnabled && (
        <Card padding="lg">
          <CardHeader>
            <Flex align="start" gap="md">
              <div className="p-2 bg-warning/10 rounded-lg">
                <Warning size={24} className="text-warning" />
              </div>
              <div className="flex-1">
                <Heading level={4}>Microsoft Teams is in private preview</Heading>
                <Text size="sm" className="text-muted-foreground mt-1">
                  This integration isn&apos;t enabled for your organization yet. You can stage your
                  Microsoft tenant id below, but inbound mentions won&apos;t be routed to {name}{' '}
                  until the <code>chat.teams</code> feature flag is enabled. Reach out to support
                  to turn it on.
                </Text>
              </div>
            </Flex>
          </CardHeader>
        </Card>
      )}

      {/* Status card */}
      <Card padding="lg">
        <CardHeader>
          <Flex align="start" gap="md">
            <div
              className={
                state === 'connected'
                  ? 'p-2 bg-success/10 rounded-lg'
                  : 'p-2 bg-warning/10 rounded-lg'
              }
            >
              {state === 'connected' ? (
                <CheckCircle size={24} className="text-success" />
              ) : (
                <Warning size={24} className="text-warning" />
              )}
            </div>
            <div className="flex-1">
              <Flex align="center" gap="sm">
                <Heading level={3}>Microsoft Teams</Heading>
                {state === 'connected' ? (
                  <TagBadge name="Connected" color="blue" hideDot />
                ) : state === 'in-catalog' ? (
                  <TagBadge name="In your tenant catalog" color="blue" hideDot />
                ) : state === 'pending' ? (
                  <TagBadge name="Tenant bound — manifest not in catalog" color="yellow" hideDot />
                ) : (
                  <TagBadge name="Not connected" color="gray" hideDot />
                )}
              </Flex>
              <Text size="sm" className="text-muted-foreground mt-1">
                {state === 'connected'
                  ? `Bot is live in ${installCount} channel${installCount === 1 ? '' : 's'} across your tenant.`
                  : state === 'in-catalog'
                    ? `${name} is published to your tenant catalog. Open in Teams and add it to a channel to finish setup.`
                    : state === 'pending'
                      ? `Tenant bound, but ${name} isn’t in your Teams catalog yet. Run the install flow again, or sideload the manifest manually under Advanced.`
                      : `One-click install: click below to grant admin consent and we’ll publish ${name} to your tenant catalog automatically.`}
              </Text>
            </div>
            {isBound && (
              <Button
                variant="destructive"
                size="sm"
                onPress={() => unbindMutation.mutate()}
                isDisabled={unbindMutation.isPending}
              >
                Disconnect
              </Button>
            )}
          </Flex>
        </CardHeader>
      </Card>

      {/* Step 1 — One-click install */}
      <Card padding="lg">
        <CardHeader>
          <Heading level={4}>Step 1 — Install {name} in your tenant</Heading>
          <Text size="sm" className="text-muted-foreground mt-1">
            Sign in as a Microsoft 365 admin in your tenant and approve the {name} app. We
            capture your tenant id <em>and</em> publish the {name} manifest to your tenant's
            Teams app catalog in one step. You only do this once per organization.
          </Text>
        </CardHeader>
        <CardContent>
          <Flex direction="column" gap="md">
            <Flex gap="sm" align="center">
              <Button
                variant="primary"
                size="sm"
                onPress={startTeamsInstall}
              >
                <MicrosoftTeamsLogo size={16} className="mr-1.5" />
                {isInCatalog
                  ? 'Re-run install (republishes the manifest)'
                  : isBound
                    ? 'Publish manifest to catalog'
                    : `Install ${name} in Teams`}
              </Button>
              {isBound && (
                <Text size="sm" className="text-muted-foreground">
                  Bound to tenant <code>{data?.tenantId}</code>.
                </Text>
              )}
            </Flex>
            <Box className="bg-muted/40 rounded-md p-3 text-sm">
              <Text className="font-semibold mb-1">What happens next:</Text>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Microsoft asks you to sign in (use a Global / Application Admin account).</li>
                <li>
                  Microsoft shows the {name} app permissions: bot messaging + publish to your
                  tenant catalog. Click <strong>Accept</strong>.
                </li>
                <li>
                  We publish {name} to your tenant's Teams catalog, bind your tenant id, and
                  flip the status above to <strong>In your tenant catalog</strong>. Then move
                  to Step 2.
                </li>
              </ol>
            </Box>
            <button
              type="button"
              onClick={() => setShowAdvanced((s) => !s)}
              className="text-sm text-muted-foreground hover:text-foreground flex items-center self-start"
            >
              {showAdvanced ? (
                <CaretDown size={14} className="mr-1" />
              ) : (
                <CaretRight size={14} className="mr-1" />
              )}
              Advanced — bind by pasting the tenant id manually
            </button>
            {showAdvanced && (
              <Box className="border-l-2 border-muted pl-4 space-y-3">
                <Text size="sm" className="text-muted-foreground">
                  Use this if admin consent isn&apos;t available for your tenant — paste the tenant
                  id from{' '}
                  <a
                    href="https://entra.microsoft.com/#view/Microsoft_AAD_IAM/TenantOverview.ReactView"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    Entra → Overview <ArrowSquareOut size={12} className="inline ml-0.5" />
                  </a>
                  .
                </Text>
                <TextField
                  label="Microsoft Tenant ID"
                  value={tenantInput}
                  onChange={(value) => setTenantInput(value)}
                  placeholder="e.g. 12345678-1234-1234-1234-123456789012"
                  description="A GUID like 12345678-1234-1234-1234-123456789012."
                />
                <Flex gap="sm">
                  <Button
                    variant="outline"
                    size="sm"
                    isDisabled={!tenantValid || !tenantChanged || bindMutation.isPending}
                    onPress={() => bindMutation.mutate({ tenantId: tenantInput.trim() })}
                  >
                    <LinkSimple size={16} className="mr-1.5" />
                    {bindMutation.isPending
                      ? 'Binding…'
                      : isBound
                        ? 'Update binding'
                        : 'Bind tenant'}
                  </Button>
                  {tenantInput && !tenantValid && (
                    <Text size="sm" className="text-warning self-center">
                      Doesn&apos;t look like a GUID — check the value.
                    </Text>
                  )}
                </Flex>
              </Box>
            )}
          </Flex>
        </CardContent>
      </Card>

      <Card padding="lg">
        <CardHeader>
          <Heading level={4}>Step 2 — Add {name} to a channel</Heading>
          <Text size="sm" className="text-muted-foreground mt-1">
            {isInCatalog
              ? `${name} is in your tenant catalog. Open it in Teams and add the bot to a channel.`
              : 'Run Step 1 first — the install button publishes the manifest to your tenant catalog automatically.'}
          </Text>
        </CardHeader>
        <CardContent>
          <Flex direction="column" gap="md">
            {isInCatalog && teamsDeepLink ? (
              <Flex gap="sm" align="center">
                <Button
                  variant="primary"
                  size="sm"
                  onPress={() => {
                    window.open(teamsDeepLink, '_blank', 'noopener,noreferrer');
                  }}
                >
                  <ArrowSquareOut size={16} className="mr-1.5" />
                  Open {name} in Teams
                </Button>
                <Text size="sm" className="text-muted-foreground">
                  Opens your tenant's app catalog page → click <strong>Add to a team</strong>.
                </Text>
              </Flex>
            ) : (
              <Flex gap="sm" align="center">
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => {
                    window.location.href = `${API_URL}/integrations/teams/manifest.zip`;
                  }}
                >
                  <DownloadSimple size={16} className="mr-1.5" />
                  Download canary-teams.zip
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onPress={() => {
                    window.open(
                      'https://admin.teams.microsoft.com/policies/manage-apps',
                      '_blank',
                      'noopener,noreferrer'
                    );
                  }}
                >
                  <ArrowSquareOut size={16} className="mr-1.5" />
                  Open Teams Admin Center
                </Button>
              </Flex>
            )}
            <Box className="bg-muted/40 rounded-md p-3 text-sm">
              <Text className="font-semibold mb-1">
                {isInCatalog ? `After clicking "Open ${name} in Teams":` : 'Manual fallback (sideload):'}
              </Text>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                {isInCatalog ? (
                  <>
                    <li>The Teams app catalog page for {name} opens in your browser.</li>
                    <li>Click <strong>Add to a team</strong> → pick the channel.</li>
                    <li>In that channel, @-mention the {name} bot to test.</li>
                  </>
                ) : (
                  <>
                    <li>Open Teams Admin Center → <strong>Teams apps</strong> → <strong>Manage apps</strong>.</li>
                    <li>Click <strong>Upload custom app</strong> (or <strong>Upload new app</strong>).</li>
                    <li>Select the <code>canary-teams.zip</code> you just downloaded.</li>
                    <li>Approve the app for your tenant.</li>
                    <li>In a Teams channel, add the {name} bot and @-mention it to test.</li>
                  </>
                )}
              </ol>
            </Box>
          </Flex>
        </CardContent>
      </Card>

      <Card padding="lg">
        <CardHeader>
          <Heading level={4}>Step 3 — Verify</Heading>
          <Text size="sm" className="text-muted-foreground mt-1">
            Once the bot is installed and you&apos;ve @-mentioned it once, the status card above
            will flip to <strong>Connected</strong>. {name} captures the tenant&apos;s service URL
            on the first inbound activity, which is required before any outbound reply works.
          </Text>
        </CardHeader>
        <CardContent>
          <Flex gap="sm" align="center">
            <Button
              variant="outline"
              size="sm"
              onPress={() => {
                void connectionQuery.refetch();
                toast.info('Refreshed connection status.');
              }}
              isDisabled={connectionQuery.isFetching}
            >
              <ClipboardText size={16} className="mr-1.5" />
              Refresh status
            </Button>
            {state === 'connected' && (
              <Text size="sm" className="text-success">
                Bot is live in {installCount} channel{installCount === 1 ? '' : 's'}.
              </Text>
            )}
          </Flex>
        </CardContent>
      </Card>
    </Box>
  );
}
